import React from 'react';
import {type IAsset} from '@netx/core-asset-next/schema';
import {getFileNameWithoutExtension} from '@netx/core-next/utils/string';
import {
	UploadSingleFileDialogContainer,
	type UploadSingleFileDialogContainerProps,
} from '@netx/core-upload-next/components/UploadSingleFile';
import {SingleFileUploadProvider} from '@netx/core-upload-next/contexts';
import {setViewData} from '../../api';
import {assetViewDefaults} from '../../defaults';
import {
	type AssetViewUploadFormValues,
	useAssetViewUploadForm,
} from '../../hooks';
import {
	AssetViewManagerActionLabel,
	AssetViewManagerTitle,
} from '../../locales';

export interface AssetViewUploadContainerProps
	extends Omit<
		UploadSingleFileDialogContainerProps<AssetViewUploadFormValues>,
		'fileTypes' | 'form' | 'onSubmit'
	> {
	asset: IAsset;
	onCompleted?: (uuid: string) => void;
}

export const AssetViewUploadContainerPrimitive = ({
	asset,
	onCompleted,
	...props
}: AssetViewUploadContainerProps) => {
	const {form, onSubmit} = useAssetViewUploadForm({
		assetId: asset.assetId,
		submit: async (data) => {
			const uuid = await setViewData({
				assetView: {
					...assetViewDefaults,
					name: getFileNameWithoutExtension(data.fileName),
					imageId: asset.assetId,
					autoTaskId: asset.assetId,
				},
				file: data.fileUri,
			});
			onCompleted?.(uuid);
		},
	});

	return (
		<UploadSingleFileDialogContainer<AssetViewUploadFormValues>
			maxSize={Infinity}
			{...props}
			form={form}
			onSubmit={onSubmit}
			FormProps={{
				confirmLabel: AssetViewManagerActionLabel.SUBMIT,
				title: AssetViewManagerTitle.CREATE,
				...props.FormProps,
			}}
			DialogProps={{
				secondaryTitle: asset.name,
				...props.DialogProps,
			}}
		/>
	);
};

export const AssetViewUploadContainer = (
	props: AssetViewUploadContainerProps,
) => {
	return (
		<SingleFileUploadProvider useTus>
			<AssetViewUploadContainerPrimitive {...props} />
		</SingleFileUploadProvider>
	);
};
